/**
 * <ParentTodoSection> — the "À faire" block of the parent home (template).
 *
 * It exists ONLY when there is something to do: no todo → the whole block, title included,
 * renders nothing. An empty state here would be a permanent reminder that nothing is wrong.
 *
 * Ordering and mutual exclusivity (summer > activation > reprise, as in the legacy banners)
 * belong to the view-model — this block renders the list it is given, in that order.
 *
 * Les tâches tiennent dans UNE carte à séparateurs : quatre cadres empilés se lisent comme quatre
 * alertes, une carte à quatre lignes se lit comme une liste.
 *
 * La carte est **à plat** : sur l'accueil, la seule surface en relief est celle des prochaines
 * séances (voir `CLAUDE.md`, « Une seule surface en relief par page »).
 *
 * Un changement de tuteur, que l'adaptateur place en tête, sort de la liste dans sa propre carte
 * teintée : il a une échéance, et le silence décide à la place de la famille.
 */
import React from "react"

import { Stack } from "../../atoms/containers/Stack"
import { Card } from "../../molecules/Card"
import { CardList } from "../../molecules/CardList"
import { List } from "../../molecules/List"
import { TodoRow } from "../../molecules/TodoRow"
import { type UIParentTodo } from "../../../ports/primary/ui/types"

export type ParentTodoSectionProps = {
    todos: UIParentTodo[]
    onTodoPress?: (todo: UIParentTodo) => void
    testID?: string
}

export function ParentTodoSection(props: ParentTodoSectionProps) {
    const { todos, onTodoPress, testID } = props
    if (todos.length === 0) return null

    const deadlines = todos.filter((todo) => todo.kind === "tutor_change")
    const others = todos.filter((todo) => todo.kind !== "tutor_change")

    return (
        <List title="À faire" testID={testID}>
            <Stack gap="3">
                {deadlines.map((todo) => (
                    <Card key={todo.id} p="0" elevation="none" background="dangerLight" leftBorderColor="danger">
                        <TodoRow todo={todo} onPress={onTodoPress} tinted />
                    </Card>
                ))}
                {others.length > 0 && (
                    <CardList>
                        {others.map((todo) => (
                            <TodoRow key={todo.id} todo={todo} onPress={onTodoPress} />
                        ))}
                    </CardList>
                )}
            </Stack>
        </List>
    )
}
